import { config } from './config.js';
import { getToken, getUser, isConfigured } from './auth.js';
import { captureError } from './sentry.js';
import { DEMO_FOLDERS, DEMO_DOCS_BY_FOLDER, DEMO_DOC_DETAILS } from './demoData.js';

// Demo mode: anonymous visitor whose library has been seeded with sample
// folders/docs so they can explore the product without signing in. The
// flag is set by app.js after seedDemo() succeeds.
function demoActive() {
  if (typeof localStorage === 'undefined') return false;
  if (isConfigured() && getUser()) return false;
  return localStorage.getItem('sayermore.demoSeeded') === '1';
}

async function authedFetch(path, opts = {}) {
  if (!isConfigured()) throw new Error('apiClient: Clerk not configured');
  const token = await getToken();
  if (!token) throw new Error('apiClient: not signed in');
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const res = await fetch(`${config.apiUrl}${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      'X-Sayermore-API-Version': config.apiVersion,
      'X-Sayermore-Client-Version': config.clientVersion,
      ...opts.headers,
    },
  });
  if (!res.ok) {
    // Keep the server's machine-readable error code. Callers branch on it —
    // notably 403 waitlist / at_capacity, which mean "this account will never
    // work" rather than "this call failed".
    let body = null;
    try { body = await res.json(); } catch { /* error body wasn't JSON */ }
    const err = new Error(`API ${res.status} ${path}`);
    err.status = res.status;
    err.code = body?.error || null;
    err.detail = body?.detail || null;
    if (res.status === 403 && (err.code === 'waitlist' || err.code === 'at_capacity')) {
      // Global, not per-call: announce once and let app.js take over the UI
      // instead of every in-flight request throwing its own toast.
      window.dispatchEvent(new CustomEvent('sayermore:access-denied', {
        detail: { code: err.code, detail: err.detail },
      }));
    }
    captureError(err, { path, status: res.status, code: err.code });
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

function signedIn() {
  return Boolean(getUser());
}

export const apiClient = {
  get enabled() {
    return isConfigured() && signedIn();
  },

  async listFolders() {
    if (!this.enabled) {
      return demoActive() ? DEMO_FOLDERS.slice() : [];
    }
    try {
      const result = await authedFetch('/v1/folders');
      return Array.isArray(result) ? result : [];
    } catch {
      return [];
    }
  },

  async listDocsInFolder(folderKey) {
    if (!this.enabled) {
      if (demoActive() && DEMO_DOCS_BY_FOLDER[folderKey]) {
        const entry = DEMO_DOCS_BY_FOLDER[folderKey];
        return { folder: entry.folder, docs: entry.docs.slice() };
      }
      return { folder: null, docs: [] };
    }
    try {
      return await authedFetch(`/v1/folders/${encodeURIComponent(folderKey)}/docs`);
    } catch {
      return { folder: null, docs: [] };
    }
  },

  async getSession(id) {
    if (!this.enabled) return null;
    return authedFetch(`/v1/sessions/${encodeURIComponent(id)}`);
  },

  async listSessions() {
    if (!this.enabled) return [];
    try {
      const result = await authedFetch('/v1/sessions');
      return Array.isArray(result) ? result : [];
    } catch {
      return [];
    }
  },

  async getDoc(folderKey, docKey) {
    if (!this.enabled) {
      if (demoActive()) {
        return DEMO_DOC_DETAILS[folderKey]?.[docKey] || null;
      }
      return null;
    }
    try {
      return await authedFetch(`/v1/folders/${encodeURIComponent(folderKey)}/docs/${encodeURIComponent(docKey)}`);
    } catch {
      return null;
    }
  },

  async pushSession(session) {
    if (!this.enabled) return null;
    return authedFetch('/v1/sessions', { method: 'POST', body: JSON.stringify(session) });
  },

  async deleteSession(id) {
    if (!this.enabled) return null;
    return authedFetch(`/v1/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // Docs that were created or updated by this session's synthesis run.
  // Returns [{ folderKey, docKey, title, preview, updatedAt, op }].
  // Empty array on failure so the UI can render a hidden expander gracefully.
  async getSessionDocs(sessionId) {
    if (!this.enabled) return [];
    try {
      const result = await authedFetch(`/v1/sessions/${encodeURIComponent(sessionId)}/docs`);
      return Array.isArray(result) ? result : [];
    } catch {
      return [];
    }
  },

  // Bug report: { transcript, metadata } → { ok, id }. Returns null
  // on failure so the caller can show a graceful toast.
  async submitBugReport({ transcript, metadata }) {
    if (!this.enabled) return null;
    try {
      return await authedFetch('/v1/bug-reports', {
        method: 'POST',
        body: JSON.stringify({ transcript, metadata: metadata || {} }),
      });
    } catch (e) {
      captureError(e, { stage: 'submitBugReport' });
      return null;
    }
  },

  // Upload audio for an existing session. Returns:
  //   { ok: true, bytes } on success
  //   { ok: false, reason: 'not_enabled' } if user's plan lacks audio storage (402)
  //   { ok: false, reason: 'unavailable' } if server R2 isn't configured (503)
  //   { ok: false, reason: 'error' } on other failures
  // Never throws — uploads are best-effort.
  async uploadAudio(sessionId, blob, filename = 'capture.webm') {
    if (!this.enabled || !blob) return { ok: false, reason: 'unavailable' };
    const form = new FormData();
    form.append('file', blob, filename);
    try {
      const result = await authedFetch(`/v1/sessions/${encodeURIComponent(sessionId)}/audio`, {
        method: 'POST',
        body: form,
      });
      return { ok: true, bytes: result?.bytes };
    } catch (e) {
      if (e?.status === 402) return { ok: false, reason: 'not_enabled' };
      if (e?.status === 503) return { ok: false, reason: 'unavailable' };
      return { ok: false, reason: 'error' };
    }
  },

  // Fetch a short-lived signed URL to stream/play the audio. Caller is
  // responsible for using the URL before it expires (5 min).
  async getAudioPlaybackUrl(sessionId) {
    if (!this.enabled) return null;
    try {
      const result = await authedFetch(`/v1/sessions/${encodeURIComponent(sessionId)}/audio`);
      return result?.url || null;
    } catch {
      return null;
    }
  },

  async transcribeAudio(audioBlob, filename = 'capture.webm') {
    if (!this.enabled || !audioBlob) return null;
    const form = new FormData();
    form.append('file', audioBlob, filename);
    form.append('filename', filename);
    return authedFetch('/v1/transcribe', { method: 'POST', body: form });
  },

  // Founder-only dashboard data (per-user costs + signups). The server
  // 404s unless the Clerk-verified email is in its ADMIN_EMAILS — the
  // hidden tab in the UI is cosmetic, this call is the real gate.
  async adminOverview() {
    if (!this.enabled) return null;
    return authedFetch('/v1/admin/overview');
  },

  // --- Public note sharing (frozen snapshots) ---
  // createShare doubles as "update shared version": the server re-snapshots
  // when a live share already exists for the doc.
  async createShare(folderKey, docKey) {
    if (!this.enabled) return null;
    return authedFetch('/v1/shares', {
      method: 'POST',
      body: JSON.stringify({ folderKey, docKey }),
    });
  },

  async listShares() {
    if (!this.enabled) return [];
    try {
      const result = await authedFetch('/v1/shares');
      return Array.isArray(result) ? result : [];
    } catch {
      return [];
    }
  },

  async revokeShare(id) {
    if (!this.enabled) return false;
    await authedFetch(`/v1/shares/${encodeURIComponent(id)}`, { method: 'DELETE' });
    return true;
  },

  // Anonymous viewer fetch for the /s/<id> page — no Clerk token.
  async fetchPublicShare(id) {
    const res = await fetch(`${config.apiUrl}/v1/shares/${encodeURIComponent(id)}/public`);
    if (!res.ok) {
      const err = new Error(`API ${res.status} share`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  },

  // Extract + repair text from one or more images of physical paper.
  // All images are sent in one request so a multi-page document becomes a
  // single combined text. Returns { text, repaired, images }.
  async extractImages(files, context = '') {
    if (!this.enabled || !files || !files.length) return null;
    const form = new FormData();
    files.forEach((f, i) => form.append('files', f, f.name || `page-${i + 1}.jpg`));
    if (context) form.append('context', context);
    return authedFetch('/v1/extract-image', { method: 'POST', body: form });
  },

  // Best-effort upload of the original images to a session (paid feature;
  // 402 if the plan doesn't include image storage). Mirrors uploadAudio.
  async getImageUrls(sessionId) {
    if (!this.enabled) return [];
    const result = await authedFetch(`/v1/sessions/${encodeURIComponent(sessionId)}/images`);
    return Array.isArray(result?.urls) ? result.urls : [];
  },

  async uploadImages(sessionId, files) {
    if (!this.enabled || !files || !files.length) return { ok: false, reason: 'unavailable' };
    const form = new FormData();
    files.forEach((f, i) => form.append('files', f, f.name || `page-${i + 1}.jpg`));
    try {
      const result = await authedFetch(`/v1/sessions/${encodeURIComponent(sessionId)}/image`, {
        method: 'POST',
        body: form,
      });
      return { ok: true, bytes: result?.bytes };
    } catch (e) {
      if (e?.status === 402) return { ok: false, reason: 'not_enabled' };
      if (e?.status === 503) return { ok: false, reason: 'unavailable' };
      return { ok: false, reason: 'error' };
    }
  },

  // Synthesize speech from text. Returns an audio Blob the caller can
  // play via URL.createObjectURL + new Audio(). Returns null on
  // failure (no auth, network error, server 5xx). Server defaults to
  // gpt-4o-mini-tts with the "sage" voice and the desktop's warmly-
  // unhurried instructions, so passing just `text` is enough.
  async synthesizeSpeech(text, opts = {}) {
    if (!this.enabled || !text || !text.trim()) return null;
    if (!isConfigured()) return null;
    const token = await getToken();
    if (!token) return null;
    try {
      const res = await fetch(`${config.apiUrl}/v1/tts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Sayermore-API-Version': config.apiVersion,
          'X-Sayermore-Client-Version': config.clientVersion,
        },
        body: JSON.stringify({
          text,
          voice: opts.voice,
          format: opts.format || 'mp3',
          instructions: opts.instructions,
        }),
      });
      if (!res.ok) {
        captureError(new Error(`TTS ${res.status}`), { stage: 'synthesizeSpeech', status: res.status });
        return null;
      }
      return await res.blob();
    } catch (e) {
      captureError(e, { stage: 'synthesizeSpeech' });
      return null;
    }
  },

  async enqueuePhotoMemory(capture, displayName) {
    const form = new FormData();
    form.append('metadata', JSON.stringify({ id: capture.id, explanation: capture.explanation || '',
      displayName, storeImages: Boolean(capture.storeImages), createdAt: capture.createdAt }));
    capture.images.forEach((blob, i) => form.append('images', blob, `photo-${i}`));
    if (capture.voiceAudio) form.append('voice', capture.voiceAudio, 'explanation');
    return authedFetch('/v1/photo-jobs', { method: 'POST', body: form });
  },
  async getPhotoMemoryJob(id) {
    return authedFetch(`/v1/photo-jobs/${encodeURIComponent(id)}`);
  },
  async retryPhotoMemory(id) {
    return authedFetch(`/v1/photo-jobs/${encodeURIComponent(id)}/retry`, { method: 'POST' });
  },

  async synthesize({ transcript, hint, sessionId, targetDocPath, displayName }) {
    if (!this.enabled) return null;
    return authedFetch('/v1/synthesize', {
      method: 'POST',
      body: JSON.stringify({ transcript, hint, sessionId, targetDocPath, displayName }),
    });
  },

  async upsertFolder(folder) {
    if (!this.enabled) return null;
    return authedFetch('/v1/folders', { method: 'POST', body: JSON.stringify(folder) });
  },

  async upsertDoc(doc) {
    if (!this.enabled) return null;
    return authedFetch('/v1/docs', { method: 'POST', body: JSON.stringify(doc) });
  },

  async deleteDoc(folderKey, docKey) {
    if (!this.enabled) return null;
    return authedFetch(
      `/v1/folders/${encodeURIComponent(folderKey)}/docs/${encodeURIComponent(docKey)}`,
      { method: 'DELETE' },
    );
  },

  async deleteFolder(folderKey) {
    if (!this.enabled) return null;
    return authedFetch(`/v1/folders/${encodeURIComponent(folderKey)}`, { method: 'DELETE' });
  },

  // Soft-delete trash. Items are restored or hard-deleted after 30 days
  // (handled by the boot-time sweep on the server).
  async getTrash() {
    if (!this.enabled) return { folders: [], docs: [], sessions: [] };
    try {
      return await authedFetch('/v1/trash');
    } catch {
      return { folders: [], docs: [], sessions: [] };
    }
  },

  async restoreTrash(payload) {
    if (!this.enabled) return null;
    return authedFetch('/v1/trash/restore', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async emptyTrash() {
    if (!this.enabled) return null;
    return authedFetch('/v1/trash', { method: 'DELETE' });
  },

  // RAG-powered Q&A over the user's library. Wraps fetch in an
  // AbortController so a stuck Anthropic call can't leave the UI spinning
  // forever. Returns { answer, citations } on success, or { error } on
  // failure. Possible error codes: client_timeout, upstream_timeout,
  // not_signed_in, network, http_<status>.
  async ask(question) {
    if (!this.enabled) return { error: 'not_signed_in' };
    const token = await getToken();
    if (!token) return { error: 'not_signed_in' };
    const controller = new AbortController();
    // 75s: matches the server's 45s upstream cap with extra headroom for
    // Render cold-starts. Past 75s a user has long since given up.
    const timeoutId = setTimeout(() => controller.abort(), 75_000);
    try {
      const res = await fetch(`${config.apiUrl}/v1/ask`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Sayermore-API-Version': config.apiVersion,
          'X-Sayermore-Client-Version': config.clientVersion,
        },
        body: JSON.stringify({ question }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 504 || body.error === 'upstream_timeout') return { error: 'upstream_timeout' };
        return { error: body.error || `http_${res.status}` };
      }
      return await res.json();
    } catch (e) {
      clearTimeout(timeoutId);
      if (e.name === 'AbortError') return { error: 'client_timeout' };
      captureError(e, { stage: 'apiClient.ask' });
      return { error: 'network' };
    }
  },

  // Passive reflections — Sayermore observing patterns over the user's
  // most recent docs. Empty array on failure for the list, null on
  // failure for generate so the caller can decide UX.
  async listReflections() {
    if (!this.enabled) return [];
    try {
      const result = await authedFetch('/v1/reflections');
      return Array.isArray(result) ? result : [];
    } catch {
      return [];
    }
  },

  async generateReflection() {
    if (!this.enabled) return null;
    try {
      return await authedFetch('/v1/reflections/generate', { method: 'POST' });
    } catch (e) {
      captureError(e, { stage: 'apiClient.generateReflection' });
      return null;
    }
  },
};

// Keep the global for legacy inline handlers that still reference it.
if (typeof window !== 'undefined') window.apiClient = apiClient;
